import { ellipsis } from "ApiConstraints";
import classNames from "classnames";
import { DisplayDateRelative } from "components/DisplayFields/DisplayDateRelative";
import { DisplayFileSize } from "components/DisplayFields/DisplayFileSize";
import { TemplatesTableViewModel } from "models/TemplatesTableViewModel";
import { useApi, useTranslations } from "providers";
import React from "react";

interface TemplatesTableProps {
  results: TemplatesTableViewModel;
}

export const TemplatesTable: React.FC<TemplatesTableProps> = ({ results }) => {
  const i18n = useTranslations();
  const { downloadApi } = useApi();

  const groupLabel = (groupKey: string) =>
    groupKey === "Meeting" ? i18n.OutputDocuments_Button_Meeting : i18n.OutputDocuments_Button_AgendaItem;

  return (
    <div>
      {results.groups.map((group, groupIndex) => {
        let { groupKey, documents } = group;
        groupKey = groupKey || "";
        documents = documents || [];

        return (
          <div key={groupKey} className={classNames({ "mt-4": groupIndex > 0 })}>
            <h5 className="card-subtitle p-0 mt-0 mb-2">{groupLabel(groupKey)}:</h5>

            <table className="table table-sm sbb-table-bs">
              <thead>
                <tr>
                  <th className="w-25">{i18n.Name}</th>
                  <th>{i18n.OutputDocument_Language}</th>
                  <th>{i18n.FileSize}</th>
                  <th className="text-nowrap">{i18n.LastModified}</th>
                </tr>
              </thead>
              {documents.length === 0 && (
                <tbody>
                  <tr>
                    <td colSpan={4}>{i18n.Table_NoRows}</td>
                  </tr>
                </tbody>
              )}

              {documents.length > 0 && (
                <tbody>
                  {documents.flatMap((doc) => {
                    const rowCount = Math.max(doc.files.length, 1);
                    const nameCell = (
                      <td rowSpan={rowCount} className="w-25 no-hover">
                        <div>
                          <i className={`file-icon ${doc.iconClass}`} /> {doc.title}
                        </div>
                        <ul className="mt-1 ps-4 mb-0">
                          <li>
                            <code>{doc.templateName}</code>
                          </li>
                          <li className="small lh-sm text-muted" title={doc.description}>
                            {doc.description.length > 150
                              ? `${doc.description.slice(0, 150)}${ellipsis}`
                              : doc.description}
                          </li>
                        </ul>
                      </td>
                    );

                    if (doc.files.length === 0) {
                      return [
                        <tr key={doc.templateName} className="no-hover">
                          {nameCell}
                          <td colSpan={3} className="text-muted">
                            {i18n.Table_NoRows}
                          </td>
                        </tr>,
                      ];
                    }

                    return doc.files.map((file, idx) => {
                      const { path, downloadUrl, fileName, language, fileSize, lastModified } = file;

                      return (
                        <tr key={path}>
                          {idx === 0 && nameCell}
                          <td>
                            {downloadUrl ? (
                              <a
                                href={downloadUrl}
                                onClick={async (e) => {
                                  e.preventDefault();
                                  await downloadApi.handleDownload(downloadUrl, fileName);
                                }}
                                className="d-inline-flex align-items-center gap-1"
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="ms-Icon ms-Icon--Download" />
                                {language || i18n.OutputDocument_Language_All}
                              </a>
                            ) : (
                              language || i18n.OutputDocument_Language_All
                            )}
                          </td>
                          <td className="small">
                            <DisplayFileSize value={fileSize} />
                          </td>
                          <td className="small text-nowrap">
                            <DisplayDateRelative value={lastModified} />
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              )}
            </table>
          </div>
        );
      })}
    </div>
  );
};
